import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useInView } from 'react-intersection-observer';
import { FaUserMd, FaClock, FaHospital, FaAward } from 'react-icons/fa';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const FeaturesSection = styled.section`
  padding: 120px ${({ theme }) => theme.spacing.lg};
  background: linear-gradient(180deg, #fefce8 0%, #fef3c7 50%, #fef9e7 100%);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 20%;
    left: -5%;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(0, 168, 168, 0.08) 0%, transparent 70%);
    border-radius: 50%;
    pointer-events: none;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 80px ${({ theme }) => theme.spacing.md};
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing['3xl']};
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const ImageColumn = styled.div`
  opacity: ${({ $inView }) => ($inView ? 1 : 0)};
  transform: translateY(${({ $inView }) => ($inView ? '0' : '30px')});
  transition: all 0.8s ease-out;
  position: relative;
  z-index: 1;
  margin-bottom: ${({ theme }) => theme.spacing['2xl']};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${({ theme }) => theme.spacing.lg};
  max-width: 1000px;
  margin: 0 auto;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: ${({ theme }) => theme.spacing.md};
  }
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing.xl};
  border: 1px solid rgba(0, 102, 204, 0.15);
  box-shadow: 
    0 8px 32px rgba(0, 102, 204, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 1);
  text-align: center;
  transition: all ${({ theme }) => theme.transitions.base};
  animation: ${fadeInUp} 0.8s ease-out backwards;
  animation-delay: ${({ $index }) => $index * 0.15}s;
  
  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 
      0 12px 40px rgba(0, 102, 204, 0.18),
      inset 0 1px 0 rgba(255, 255, 255, 1);
    border-color: rgba(0, 102, 204, 0.25);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.lg};
  }
`;

const StatNumber = styled.div`
  font-size: ${({ theme }) => theme.fontSizes['4xl']};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  line-height: 1.2;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.fontSizes['3xl']};
  }
`;

const StatLabel = styled.div`
  color: #64748b;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  line-height: 1.5;
`;

const SectionBadge = styled.div`
  display: inline-block;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  background: rgba(0, 102, 204, 0.1);
  border-radius: ${({ theme }) => theme.borderRadius.full};
  border: 1px solid rgba(0, 102, 204, 0.2);
`;

const SectionTitle = styled.h2`
  color: #1e293b;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.fontSizes['4xl']};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  line-height: 1.2;
  
  span {
    background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.fontSizes['3xl']};
  }
`;

const SectionDescription = styled.p`
  color: #64748b;
  font-size: ${({ theme }) => theme.fontSizes.lg};
  line-height: 1.8;
`;

const FeaturesList = styled.ul`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing.lg};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const FeatureItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.xl};
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(20px);
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  border: 1px solid rgba(0, 102, 204, 0.1);
  box-shadow: 
    0 4px 20px rgba(0, 102, 204, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  transition: all ${({ theme }) => theme.transitions.base};
  animation: ${fadeInUp} 0.6s ease-out backwards;
  animation-delay: ${({ $index }) => $index * 0.1}s;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 0;
    background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
    transition: height 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 
      0 8px 30px rgba(0, 102, 204, 0.15),
      inset 0 1px 0 rgba(255, 255, 255, 1);
    border-color: rgba(0, 102, 204, 0.2);
    background: rgba(255, 255, 255, 0.95);
    
    &::before {
      height: 100%;
    }
  }
`;

const FeatureIcon = styled.div`
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  box-shadow: 0 8px 20px rgba(0, 102, 204, 0.25);
  transition: all ${({ theme }) => theme.transitions.base};
  
  ${FeatureItem}:hover & {
    transform: scale(1.05);
    box-shadow: 0 10px 25px rgba(0, 102, 204, 0.35);
  }
`;

const FeatureContent = styled.div`
  flex: 1;
`;

const FeatureTitle = styled.h4`
  color: #1e293b;
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const FeatureDescription = styled.p`
  color: #64748b;
  font-size: ${({ theme }) => theme.fontSizes.base};
  line-height: 1.7;
  margin: 0;
`;

const features = [
  {
    icon: <FaUserMd />,
    title: 'Save on Administrative Costs',
    description: 'U.S. healthcare administrative costs are projected to reach $79.6 billion to $307.9 billion annually. LOGG helps reduce these by streamlining claims, coding, and documentation processes.'
  },
  {
    icon: <FaClock />,
    title: 'Reduce Denials & Maximize Payments',
    description: 'Annually, $400 - $600 billion worth of claims are expected to be denied due to errors and eligibility issues. LOGG`s proactive denial management ensures fewer denials and faster payments.'
  },
  {
    icon: <FaHospital />,
    title: 'Recover Unpaid Claims',
    description: 'Approximately 10-15% of claims remain unpaid, totaling $400–$500 billion annually. LOGG optimizes collections, helping you recover more from unpaid claims and patient balances.'
  },
  {
    icon: <FaAward />,
    title: 'Cut Resubmission Costs',
    description: '$600 million to $3.4 billion is spent annually correcting claims. LOGG minimizes resubmissions and ensures claims are processed the first time, saving you time and money.'
  }
];

const stats = [
  { number: '99%', label: 'Clean Claims Rate' },
  { number: '30%', label: 'Faster Revenue Collection' },
  { number: '200+', label: 'Healthcare Providers' },
  { number: '$50M+', label: 'Revenue Recovered' }
];

export const Features = () => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  return (
    <FeaturesSection id="features" ref={ref}>
      <Container>
        <SectionHeader>
          <SectionBadge>Why Choose Us</SectionBadge>
          <SectionTitle>
            Why Partner with <span>Logg Healthcare?</span>
          </SectionTitle>
          <SectionDescription>
            Exceptional healthcare requires more than excellent patient care. We redefine Revenue Cycle Management to help you optimize your back-office operations.
          </SectionDescription>
        </SectionHeader>
        
        <ImageColumn $inView={inView}>
          <StatsGrid>
            {stats.map((stat, index) => (
              <StatCard key={index} $index={index}>
                <StatNumber>{stat.number}</StatNumber>
                <StatLabel>{stat.label}</StatLabel>
              </StatCard>
            ))}
          </StatsGrid>
        </ImageColumn>
        
        <FeaturesList>
          {features.map((feature, index) => (
            <FeatureItem key={index} $index={index}>
              <FeatureIcon aria-hidden="true">
                {feature.icon}
              </FeatureIcon>
              <FeatureContent>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureContent>
            </FeatureItem>
          ))}
        </FeaturesList>
      </Container>
    </FeaturesSection>
  );
};
